import * as React from "react";

import { Atom, useAtomValue, useSetAtom } from 'jotai';
import last from 'lodash/last.js';

import { isMirroredNode, isReferenceNode, isRegularNode, SchemaFragment, SchemaNode } from '@stoplight/json-schema-tree';
import { Box, Flex, NodeAnnotation, VStack } from '@stoplight/mosaic';

import { MenuItem, Select, TextField, Tooltip } from '@mui/material';

import { useRef, useState } from "react";
import { COMBINER_NAME_MAP } from '../../consts';
import { useJSVOptionsContext } from '../../contexts';
import { getNodeId, getOriginalNodeId } from '../../hash';
import { isPropertyRequired, visibleChildren } from '../../tree';
import { extractVendorExtensions } from '../../utils/extractVendorExtensions';
import { Caret, Description, getValidationsFromSchema, Types, Validations } from '../shared';
import { ChildStack } from '../shared/ChildStack';
import { Error } from '../shared/Error';
import { Properties, useHasProperties } from '../shared/Properties';
import { SchemaRowProps } from '../shared/SharedTypes';
import { hoveredNodeAtom, isNodeHoveredAtom, modifyDataObject, sourceDataObject, updateDataObject, updateWithPath } from './state';
import { useChoices } from './useChoices';


export const SchemaRow: React.FunctionComponent<SchemaRowProps> = React.memo(
  ({ schemaNode, nestingLevel, pl, parentNodeId, parentChangeType, sourceSchemaDestination, selectedVariable, previousNodeId, setSelectedVariable }) => {
    const {
      defaultExpandedDepth,
      renderRowAddon,
      renderExtensionAddon,
      onGoToRef,
      hideExamples,
      renderRootTreeLines,
      nodeHasChanged,
      viewMode,
    } = useJSVOptionsContext();

    const setHoveredNode = useSetAtom(hoveredNodeAtom);

    const nodeId = getNodeId(schemaNode, parentNodeId);

    const textFieldRef = useRef<HTMLInputElement>(null);
    const [isHovered, setIsHovered] = useState(false);

    const resetSelectedVariable = {
      variable_type: "",
      variable_value: null,
      variable_path: "",
      nodeId: selectedVariable.nodeId
    };

    // @ts-expect-error originalFragment does exist...
    const originalNodeId = schemaNode.originalFragment?.$ref ? getOriginalNodeId(schemaNode, parentNodeId) : nodeId;
    const mode = viewMode === 'standalone' ? undefined : viewMode;
    const hasChanged = nodeHasChanged?.({ nodeId: originalNodeId, mode });

    const [isExpanded, setExpanded] = React.useState<boolean>(
      !isMirroredNode(schemaNode) && nestingLevel <= defaultExpandedDepth,
    );

    const { selectedChoice, setSelectedChoice, choices } = useChoices(schemaNode);
    const typeToShow = selectedChoice.type;
    const description = isRegularNode(typeToShow) ? typeToShow.annotations.description : null;

    const rootLevel = renderRootTreeLines ? 1 : 2;
    const childNodes = React.useMemo(() => visibleChildren(typeToShow), [typeToShow]);
    const combiner = isRegularNode(schemaNode) && schemaNode.combiners?.length ? schemaNode.combiners[0] : null;
    const isCollapsible = childNodes.length > 0;
    const isRootLevel = nestingLevel < rootLevel;

    const required = isPropertyRequired(schemaNode);
    const deprecated = isRegularNode(schemaNode) && schemaNode.deprecated;
    const validations = isRegularNode(schemaNode) ? schemaNode.validations : {};
    const hasProperties = useHasProperties({ required, deprecated, validations });

    const [totalVendorExtensions, vendorExtensions] = React.useMemo(
      () => extractVendorExtensions(schemaNode.fragment),
      [schemaNode.fragment],
    );
    const hasVendorProperties = totalVendorExtensions > 0;

    const annotationRootOffset = renderRootTreeLines ? 0 : 8;
    let annotationLeftOffset = -20 - annotationRootOffset;
    if (nestingLevel > 1) {
      // annotationLeftOffset -= 27;
      annotationLeftOffset =
        -1 * 29 * Math.max(nestingLevel - 1, 1) - Math.min(nestingLevel, 2) * 2 - 16 - annotationRootOffset;

      if (!renderRootTreeLines) {
        annotationLeftOffset += 27;
      }
    }

    if (parentChangeType === 'added' && hasChanged && hasChanged.type === 'removed') {
      return null;
    }

    if (parentChangeType === 'removed' && hasChanged && hasChanged.type === 'added') {
      return null;
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (
        selectedVariable?.variable_type !== "" &&
        selectedVariable?.variable_type !== selectedChoice.title
      ) {
        setSelectedVariable(resetSelectedVariable);
        alert(
          `Can't map ${selectedVariable.variable_type} type to ${selectedChoice.title}. Please check the types of the variables.`
        );
      } else {
        if (
          selectedVariable?.variable_value?.value &&
          e.currentTarget.name === selectedVariable.variable_value.name
        ) {
          e.currentTarget.value = selectedVariable.variable_value.value;
        }
        setSelectedVariable({
          variable_value: e.currentTarget,
          variable_type: selectedChoice.title,
          variable_path: modifyDataObject(schemaNode, []),
          nodeId: selectedVariable.nodeId
        });
      }
    };

    const handleClick = (schemaNode: SchemaNode) => {
      const value = `$.${modifyDataObject(schemaNode, [])}`;
      if (
        selectedVariable.variable_type === selectedChoice.title &&
        selectedVariable.variable_value
      ) {
        const updatedValue = sourceSchemaDestination === 'root'
          ? (value as string).substring(0, 1) + 'root' + (value as string).substring(1)
          : (value as string).substring(0, 1) + previousNodeId + (value as string).substring(1)

        console.log('saving with click: ', selectedVariable.nodeId + '.' + selectedVariable.variable_path, updatedValue)
        updateWithPath(selectedVariable.nodeId + '.' + selectedVariable.variable_path, updatedValue);
        selectedVariable.variable_value.value = updatedValue;
      } else if (selectedVariable.variable_type !== "") {
        setSelectedVariable(resetSelectedVariable);
        alert(
          `Can't map ${selectedVariable.variable_type} type to ${selectedChoice.title}. Please check the types of the variables.`
        );
      }
    };

    const intializeDefaultValue = () => {
      if (sourceSchemaDestination) {
        const path = `$root.${modifyDataObject(schemaNode, [])}`

        updateDataObject(schemaNode, path, selectedVariable.nodeId);
        return path;
      }
    }

    return (
      <>
        <Flex
          maxW="full"
          pl={pl}
          py={2}
          data-id={originalNodeId}
          data-test="schema-row"
          pos="relative"
          onMouseEnter={(e: any) => {
            e.stopPropagation();
            setHoveredNode(selectedChoice.type);
          }}
        >
          {!isRootLevel && <Box borderT w={isCollapsible ? 1 : 3} ml={-3} mr={3} mt={2} />}
          {parentChangeType !== 'added' && parentChangeType !== 'removed' ? (
            <NodeAnnotation change={hasChanged} style={{ left: annotationLeftOffset }} />
          ) : null}
          <VStack spacing={1} maxW="full" flex={1} ml={isCollapsible && !isRootLevel ? 2 : undefined}>
            <Flex
              alignItems="center"
              maxW="full"
              onClick={isCollapsible ? () => setExpanded(!isExpanded) : undefined}
              cursor={isCollapsible ? 'pointer' : undefined}
            >
              {isCollapsible ? <Caret isExpanded={isExpanded} /> : null}
              <Flex alignItems="baseline" fontSize="base">
                {schemaNode.subpath.length > 0 && shouldShowPropertyName(schemaNode) && (
                  <Tooltip
                    title={viewMode === 'standalone' && selectedChoice.title !== 'object' ?
                      `$.${modifyDataObject(schemaNode, [])}` : ""}
                    arrow
                  >
                    <Box
                      mr={2}
                      fontFamily="mono"
                      fontWeight="semibold"
                      data-test={`property-name-${last(schemaNode.subpath)}`}
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                      style={
                        viewMode === "standalone" &&
                          selectedChoice.title !== "object" && isHovered
                          ? {
                            cursor: 'pointer', textDecoration: "underline", background: 'rgba(0, 0, 0, 0.1)', border: '1px dashed rgba(0, 0, 0, 0.5)', padding: '5px 10px', borderRadius: '5px',
                            transition: 'background-color 0.3s ease, box-shadow 0.3s ease'
                          }
                          : {}
                      }
                      onClick={() => {
                        if (
                          viewMode === "standalone" &&
                          selectedChoice.title !== "object"
                        ) {
                          handleClick(schemaNode);
                        }
                      }}
                      onDragStart={(e: React.DragEvent<HTMLDivElement>) => {
                        const dataToTransfer = JSON.stringify({
                          pathData: `$.${modifyDataObject(schemaNode, [])}`,
                          dataType: selectedChoice.title
                        });
                        e.dataTransfer.setData("application/json", dataToTransfer);
                      }}
                      draggable={viewMode === "standalone" && selectedChoice.title !== "object" ? "true" : "false"}
                    >
                      {last(schemaNode.subpath)}
                    </Box>
                  </Tooltip>
                )}

                {choices.length === 1 && <Types schemaNode={typeToShow} />}

                {viewMode === "write" &&
                  selectedChoice.title !== "object" &&
                  ((selectedChoice.title === "array[object]" && !isCollapsible) || selectedChoice.title !== "array[object]") &&
                  (
                    <TextField
                      variant="outlined"
                      defaultValue={
                        (schemaNode.fragment as SchemaFragment as any)?.source ||
                        sourceDataObject[`${selectedVariable.nodeId}.${modifyDataObject(schemaNode, [])}`] ||
                        intializeDefaultValue()
                      }
                      name={modifyDataObject(schemaNode, [])}
                      onFocus={handleFocus}
                      // onChange={handleChange}
                      onDragOver={(e: React.DragEvent<HTMLInputElement>) => {
                        e.preventDefault();
                      }}
                      onDrop={(e: React.DragEvent<HTMLInputElement>) => {
                        const droppedData = e.dataTransfer.getData("application/json");
                        if (droppedData) {
                          try {
                            const parsedData = JSON.parse(droppedData);
                            const { pathData, dataType } = parsedData;
                            const updatedPath = sourceSchemaDestination === 'root'
                              ? (pathData as string).substring(0, 1) + 'root' + (pathData as string).substring(1)
                              : (pathData as string).substring(0, 1) + previousNodeId + (pathData as string).substring(1);

                            if (dataType === selectedChoice.title) {
                              if (textFieldRef.current) {
                                textFieldRef.current.value = updatedPath;
                              }
                              updateDataObject(schemaNode, updatedPath, selectedVariable.nodeId);
                            } else {
                              alert(`Can't map ${dataType} type to ${selectedChoice.title}. Please check the types of the variables.`);
                            }
                          } catch (error) {
                            console.error("Error parsing dropped data: ", error);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        // const value = '$.' + e.target.value.split('.').slice(1).join('.');
                        // const updatedValue = sourceSchemaDestination === 'root'
                        //   ? (value as string).substring(0, 1) + 'root' + (value as string).substring(1)
                        //   : (value as string).substring(0, 1) + previousNodeId + (value as string).substring(1);

                        // console.log("value on blur: ", updatedValue);
                        console.log("value on blur: ", e.target.value);
                        updateDataObject(schemaNode, e.target.value, selectedVariable.nodeId);
                      }}
                      placeholder="Enter source path"
                      size="small"
                      inputRef={textFieldRef}
                      sx={{
                        marginLeft: "8px",
                        marginRight: "8px",
                      }}
                    // required
                    />
                  )}
                {onGoToRef && isReferenceNode(schemaNode) && schemaNode.external ? (
                  <Box
                    as="a"
                    ml={2}
                    cursor="pointer"
                    color="primary-light"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onGoToRef(schemaNode);
                    }}
                  >
                    (go to ref)
                  </Box>
                ) : null}

                {schemaNode.subpath.length > 1 && schemaNode.subpath[0] === 'patternProperties' ? (
                  <Box ml={2} color="muted">
                    (pattern property)
                  </Box>
                ) : null}

                {choices.length > 1 && (
                  <Select
                    aria-label="Pick a type"
                    size="small"
                    value={String(choices.indexOf(selectedChoice))}
                    onChange={(e) => setSelectedChoice(choices[Number(e.target.value)])}
                    displayEmpty
                  >
                    {choices.map((choice, index) => (
                      <MenuItem key={index} value={String(index)}>
                        {combiner ? `${COMBINER_NAME_MAP[combiner]}: ` : ''}{choice.title}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Flex>
              {hasProperties && <Divider atom={isNodeHoveredAtom(schemaNode)} />}
              <Properties required={required} deprecated={deprecated} validations={validations} />
            </Flex>
            {typeof description === 'string' && description.length > 0 && <Description value={description} />}
            <Validations
              validations={isRegularNode(schemaNode) ? getValidationsFromSchema(schemaNode) : {}}
              hideExamples={hideExamples}
            />
            {hasVendorProperties && renderExtensionAddon ? (
              <Box>{renderExtensionAddon({ schemaNode, nestingLevel, vendorExtensions })}</Box>
            ) : null}
          </VStack>
          <Error schemaNode={schemaNode} />
          {renderRowAddon ? <Box>{renderRowAddon({ schemaNode, nestingLevel })}</Box> : null}
        </Flex>
        {isCollapsible && isExpanded ? (
          <ChildStack
            schemaNode={schemaNode}
            childNodes={childNodes}
            currentNestingLevel={nestingLevel}
            parentNodeId={nodeId}
            RowComponent={SchemaRow}
            sourceSchemaDestination={sourceSchemaDestination}
            previousNodeId={previousNodeId}
            selectedVariable={selectedVariable}
            setSelectedVariable={setSelectedVariable}
            parentChangeType={parentChangeType ? parentChangeType : hasChanged ? hasChanged?.type : undefined}
          />
        ) : null}
      </>
    );
  },
);

const Divider = ({ atom }: { atom: Atom<boolean> }) => {
  const isHovering = useAtomValue(atom);

  return <Box bg={isHovering ? 'canvas-200' : undefined} h="px" flex={1} mx={3} />;
};

function shouldShowPropertyName(schemaNode: SchemaNode) {
  return (
    schemaNode.subpath.length === 2 &&
    (schemaNode.subpath[0] === 'properties' || schemaNode.subpath[0] === 'patternProperties')
  );
}
